// components/layout/AppBar.js
import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../auth/AuthContext';
import { clearRetryCounts } from '../../utils/axiosInstance';
import { stopNotificationPolling } from '../../utils/notificationPolling';
import './AppBar.css';
import logo from '../../assets/logo_white.png'; // Make sure to add your logo to assets folder
import NotificationBell from './NotificationBell';

/** Same preset shape as internal-dashboard → All Tasks (Assigned to Me + statuses). */
const MY_ASSIGNMENTS_ALL_TASKS_STATE = {
  from: "internal-dashboard",
  preset: {
    selectedStatuses: ["In Progress", "Not started"],
    ownershipFilter: "Assigned",
    searchMode: "advanced",
    searchPanelExpanded: true,
    applicationIds: [],
    selectedPriorities: [],
    selectedTaskTypes: [],
    searchTerm: "",
    internalOverdueClientFilter: false,
    internalUnassignedClientFilter: false,
    releaseDateFilter: "",
  },
};

const AppBar = ({ userName: propUserName = "", userRole: propUserRole = "" }) => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const [userName, setUserName] = useState(propUserName);
  const [userRole, setUserRole] = useState(propUserRole);
  const [navOpen, setNavOpen] = useState(false);
  
  // Update state from localStorage on mount and when props change
  useEffect(() => {
    // Get user data from localStorage (keep original userName, get role name)
    const storedUserName = localStorage.getItem('userName') || propUserName;
    const storedRoleName = localStorage.getItem('user_role_name') || 
                          localStorage.getItem('userRole') || 
                          propUserRole;
    
    setUserName(storedUserName);
    setUserRole(storedRoleName);
  }, [propUserName, propUserRole]); // Re-run when props change

  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setNavOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [navOpen]);

  const handleHome = useCallback(() => {
    navigate("/");
    setNavOpen(false);
  }, [navigate]);

  const handleMyAssignments = useCallback(() => {
    navigate("/all-tasks", { state: MY_ASSIGNMENTS_ALL_TASKS_STATE });
    setNavOpen(false);
  }, [navigate]);

  const handleTicketAgeingReport = useCallback(() => {
    navigate("/ticket-ageing-report");
    setNavOpen(false);
  }, [navigate]);
  
  // Format the user name properly (capitalize first and last name)
  const formatName = (name) => {
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    
    const firstName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    const lastName = parts[parts.length - 1].charAt(0).toUpperCase() + parts[parts.length - 1].slice(1);
    
    return `${firstName} ${lastName}`;
  };
  
  // Format role with capitalized first letter
  const formatRole = (role) => {
    if (!role) return '';
    return role.charAt(0).toUpperCase() + role.slice(1);
  };
  
  const handleLogout = () => {
    try {
      sessionStorage.clear();
      clearRetryCounts();
      stopNotificationPolling();
      setUserName('');
      setUserRole('');

      // AuthService.logout → logoutLocal clears entire localStorage (tokens, role picker key, filters, etc.)
      if (logout) {
        logout();
      } else {
        try {
          localStorage.clear();
        } catch (e) {
          console.warn(e);
        }
        navigate('/login');
      }
    } catch (error) {
      console.error('Error during logout:', error);
      navigate('/login');
    }
  };
  
  const formattedName = userName; // Keep original username without formatting
  const formattedRole = formatRole(userRole);
  
  return (
    <div className="appbar-container">
      <div className="appbar-content">
        <div className="appbar-left-cluster">
          <button
            type="button"
            className="appbar-breadcrumb-trigger"
            onClick={() => setNavOpen(true)}
            aria-expanded={navOpen}
            aria-controls="appbar-nav-drawer"
            aria-label="Open navigation menu"
          >
            <span className="appbar-breadcrumb-icon" aria-hidden="true">
              <i className="fas fa-bars" />
            </span>
          </button>
          <div className="appbar-logo">
            <img src={logo} alt="Logo" height="75" />
          </div>
        </div>
        
        {/* Flexible space */}
        <div className="appbar-spacer"></div>
        
        <div className="login-title">
          <span className="login-apps-text">APPS TASK TRACKER</span>
        </div>
        
        {/* Flexible space */}
        <div className="appbar-spacer"></div>
        
        {/* Right side - Username, role and logout */}
        <div className="appbar-user-section">
          <div className="appbar-user-info">
            <div className="appbar-username">
              Welcome {formattedName}! 
              {/* {userRole && <span className="appbar-role" style={{ color: '#ff8c00', marginLeft: '8px' }}>({formattedRole})</span>} */}
            </div>
          </div>
          <NotificationBell />
          <div className="appbar-logout" onClick={handleLogout} title="Logout">
            <i className="fas fa-sign-out-alt"></i>
            {/* <span className="logout-text">Logout</span> */}
          </div>
        </div>
      </div>
      <div className="appbar-border"></div>

      {navOpen ? (
        <div className="appbar-nav-root" role="presentation">
          <button
            type="button"
            className="appbar-nav-overlay"
            aria-label="Close navigation"
            onClick={() => setNavOpen(false)}
          />
          <aside
            id="appbar-nav-drawer"
            className="appbar-nav-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="appbar-nav-title"
          >
            <div className="appbar-nav-drawer-head">
              <h2 id="appbar-nav-title" className="appbar-nav-drawer-title">
                Navigation
              </h2>
              <button
                type="button"
                className="appbar-nav-close"
                onClick={() => setNavOpen(false)}
                aria-label="Close navigation"
              >
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            </div>
            <nav className="appbar-nav-menu" aria-label="Main navigation">
              <button type="button" className="appbar-nav-item" onClick={handleHome}>
                <i className="fas fa-home appbar-nav-item-icon" aria-hidden="true" />
                <span>Home</span>
              </button>
              <button type="button" className="appbar-nav-item" onClick={handleMyAssignments}>
                <i className="fas fa-list-ul appbar-nav-item-icon" aria-hidden="true" />
                <span>My Assignments</span>
              </button>
              <button type="button" className="appbar-nav-item" onClick={handleTicketAgeingReport}>
                <i className="fas fa-chart-bar appbar-nav-item-icon" aria-hidden="true" />
                <span>Ticket Ageing Report</span>
              </button>
            </nav>
          </aside>
        </div>
      ) : null}
    </div>
  );
};

export default AppBar;